import {
  Query,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useToast, type ToastProps } from "@validereinc/common-components";
import {
  ResourceAdapterType,
  ResourceDefinitions,
  ResourceType,
} from "@validereinc/domain";

export const DEFAULT_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  // If this is false, queries which are unmounted skip refetching on invalidate
  refetchOnMount: true,
  staleTime: 1 * 60 * 1000,
  retry: false,
} as const;

export const NON_INVALIDATING_QUERY_OPTIONS = {
  ...DEFAULT_QUERY_OPTIONS,
  staleTime: Infinity,
} as const;

export const DEFAULT_INVALIDATE_OPTIONS = {
  /** invalidate queries which are not mounted */
  refetchType: "all",
} as const;

export const getPollingOptions = (
  terminalStates: string[],
  statusAttribute = "status"
) => {
  return {
    /** If the entity isn't in a terminal state, refetch periodically */
    refetchIntervalInBackground: true,
    // TODO: Get useQuery typing for data/query
    refetchInterval: (data: any, query: any) =>
      data && !terminalStates.includes(data?.data?.[statusAttribute])
        ? query.state.dataUpdateCount > 5
          ? 3500
          : 1500
        : false,
  };
};

export type UseMutationCallbackType<TQueryReturnType, TError = unknown> = {
  onSuccess?: (
    data: TQueryReturnType,
    variables: any,
    context: unknown
  ) => void;
  onError?: (err: TError, variables: any, context: unknown) => void;
  successMessage?:
    | ToastProps["description"]
    | ((
        data: TQueryReturnType,
        variables: any,
        context: unknown
      ) => ToastProps["description"]);
  errorMessage?:
    | ToastProps["description"]
    | ((
        err: TError,
        variables: any,
        context: unknown
      ) => ToastProps["description"]);
  noAlerts?: boolean;
  customInvalidateFunction?: () => void;
};

export type UseQueryOptionsType<
  TQueryReturnType,
  TSelectReturnType = TQueryReturnType,
  TQueryParamType = any,
> = {
  enabled?: boolean;
  select?: <TSelectType>(data: TQueryReturnType) => TSelectType;
  refetchInterval?:
    | number
    | false
    | ((
        data: TSelectReturnType | undefined,
        query: Query<
          TQueryReturnType,
          TQueryReturnType,
          TQueryReturnType,
          [...string[], TQueryParamType]
        >
      ) => number | false);
  refetchIntervalInBackground?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
  retry?: boolean | number | ((failureCount: number, error: any) => boolean);
  retryDelay?: number | ((retryAttempt: number, error: any) => number);
};

export const useClearCache = (rootQueryKey: string | string[]) => {
  const queryClient = useQueryClient();
  const rootQueryKeys = Array.isArray(rootQueryKey)
    ? rootQueryKey
    : [rootQueryKey];

  return {
    invalidate: (queryKey: string[] = []) => {
      queryClient.invalidateQueries({
        queryKey: [...rootQueryKeys, ...queryKey],
        ...DEFAULT_INVALIDATE_OPTIONS,
      });
    },
  };
};

export const useQueryWrapper =
  <TQueryParamType, TQueryReturnType, TSelectReturnType = TQueryReturnType>(
    /** The domain method that performs the API call.
     *  Usually in the form `MyDomain.subdomain.getOne` */
    domainMethod: (inputParams: TQueryParamType) => Promise<TQueryReturnType>,
    /** The top-level cache keys for the domain.
     *  `"MyDomain.getOne"` => `["MyDomain", "getOne"]` */
    queryKeyPrefixes: string[],
    /** Set to true if you don't want the cache to ever go stale */
    isNonInvalidating = false
  ) =>
  (
    params: TQueryParamType,
    options: {
      enabled?: boolean;
      refetchInterval?:
        | number
        | false
        | ((
            data: TSelectReturnType | undefined,
            query: Query<
              TQueryReturnType,
              TQueryReturnType,
              TQueryReturnType,
              [...string[], TQueryParamType]
            >
          ) => number | false);
      refetchIntervalInBackground?: boolean;
      select?: (data: TQueryReturnType) => TSelectReturnType;
      retry?:
        | boolean
        | number
        | ((failureCount: number, error: any) => boolean);
      retryDelay?: number | ((retryAttempt: number, error: any) => number);
    } = {}
  ) =>
    useQuery<
      TQueryReturnType,
      TQueryReturnType,
      TSelectReturnType,
      [...string[], TQueryParamType]
    >(
      [...queryKeyPrefixes, params],
      ({ queryKey }) => {
        const [queryParams] = queryKey.slice(-1);
        return domainMethod(queryParams as TQueryParamType);
      },
      {
        ...(isNonInvalidating
          ? NON_INVALIDATING_QUERY_OPTIONS
          : DEFAULT_QUERY_OPTIONS),
        ...options,
        meta: {
          hideErrorToasts: true,
        },
      }
    );

export const useMutationWrapper = <TMutationParamType, TMutationReturnType>(
  /** The domain method that performs the API call.
   *  Usually in the form `MyDomain.subdomain.updateOne` */
  domainMethod: (
    inputParams: TMutationParamType
  ) => Promise<TMutationReturnType>,
  /** The query keys to invalidate */
  queryKeys: string[],
  {
    successMessage,
    errorMessage,
    onSuccess,
    onError,
    noAlerts,
    customInvalidateFunction,
  }: UseMutationCallbackType<TMutationReturnType> = {}
) => {
  const { toast } = useToast();
  const { invalidate } = useClearCache(queryKeys);

  return useMutation({
    mutationFn: (params: TMutationParamType) => domainMethod(params),
    onSuccess: (data, variables, context) => {
      if (!noAlerts) {
        const generatedSuccessMessage =
          typeof successMessage === "function"
            ? successMessage(data, variables, context)
            : successMessage;

        toast.push({
          intent: "success",
          description:
            generatedSuccessMessage ?? "Successfully completed operation",
        });
      }
      (customInvalidateFunction ?? invalidate)();
      onSuccess?.(data, variables, context);
    },
    onError: (err, variables, context) => {
      if (!noAlerts) {
        const generatedErrorMessage =
          typeof errorMessage === "function"
            ? errorMessage(err, variables, context)
            : errorMessage;
        toast.push({
          intent: "error",
          description: generatedErrorMessage ?? "Failed to complete operation",
        });
      }
      onError?.(err, variables, context);
    },
  });
};

export const useGetOne = <TDomainModel>(
  getOne: ResourceAdapterType<TDomainModel>["getOne"],
  resource: ResourceType,
  ...subdomain: string[]
) => {
  const queryKeyRoot = [resource, ...subdomain];
  return (
    params: Parameters<typeof getOne>[0],
    options: UseQueryOptionsType<Awaited<ReturnType<typeof getOne>>> = {}
  ) =>
    useQueryWrapper(getOne, [...queryKeyRoot, "getOne"])(params, {
      ...options,
      // Never allow the query to fire if the id is not provided
      enabled: Boolean(params?.id) && (options.enabled ?? true),
    });
};

export const useGetList = <T extends (params: any) => Promise<any>>(
  getList: T,
  resource: ResourceType,
  ...subdomain: string[]
) => {
  const queryKeyRoot = [resource, ...subdomain];
  return useQueryWrapper<Parameters<T>[0], unknown, Awaited<ReturnType<T>>>(
    getList,
    [...queryKeyRoot, "getList"]
  );
};

export const useUpdateOne = <TDomainModel>(
  updateOne: ResourceAdapterType<TDomainModel>["updateOne"],
  resource: ResourceType,
  ...subdomain: string[]
) => {
  const queryKeyRoot = [resource, ...subdomain];
  const getResourceName = (resource: ResourceType) =>
    ResourceDefinitions[resource].label.singular;

  type inputType = Parameters<typeof updateOne>[0];
  type returnType = Awaited<ReturnType<typeof updateOne>>;

  return (options: UseMutationCallbackType<returnType> = {}) =>
    useMutationWrapper<inputType, returnType>(updateOne, queryKeyRoot, {
      successMessage: `Successfully updated ${getResourceName(resource)}`,
      errorMessage: `Failed to update ${getResourceName(resource)}`,
      ...options,
    });
};

export const useDeleteOne = <TDomainModel>(
  deleteOne: ResourceAdapterType<TDomainModel>["deleteOne"],
  resource: ResourceType,
  ...subdomain: string[]
) => {
  const queryKeyRoot = [resource, ...subdomain];
  const getResourceName = (resource: ResourceType) =>
    ResourceDefinitions[resource].label.singular;

  type inputType = Parameters<typeof deleteOne>[0];
  type returnType = Awaited<ReturnType<typeof deleteOne>>;

  return (options: UseMutationCallbackType<returnType> = {}) =>
    useMutationWrapper<inputType, returnType>(deleteOne, queryKeyRoot, {
      successMessage: `Successfully deleted ${getResourceName(resource)}`,
      errorMessage: `Failed to delete ${getResourceName(resource)}`,
      ...options,
    });
};

export const useCreateOne = <TDomainModel>(
  createOne: ResourceAdapterType<TDomainModel>["createOne"],
  resource: ResourceType,
  ...subdomain: string[]
) => {
  const queryKeyRoot = [resource, ...subdomain];
  const getResourceName = (resource: ResourceType) =>
    ResourceDefinitions[resource].label.singular;

  type inputType = Parameters<typeof createOne>[0];
  type returnType = Awaited<ReturnType<typeof createOne>>;

  return (options: UseMutationCallbackType<returnType> = {}) =>
    useMutationWrapper<inputType, returnType>(createOne, queryKeyRoot, {
      successMessage: `Successfully created ${getResourceName(resource)}`,
      errorMessage: `Failed to create ${getResourceName(resource)}`,
      ...options,
    });
};
